import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProPaywall from "@/components/ProPaywall";
import SavedFoodsQuickLog from "@/components/SavedFoodsQuickLog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STEP_GOAL = 10000;
const EMPTY_DAY = { breakfast: "", lunch: "", dinner: "", snacks: [] };
const MEAL_SLOTS = [
  { key: "breakfast", label: "Breakfast", icon: "🌅" },
  { key: "lunch", label: "Lunch", icon: "🌞" },
  { key: "dinner", label: "Dinner", icon: "🌙" },
];

// ~0.000414 kcal per step per kg body weight (well-established estimate)
const burnedKcal = (steps, weightKg) => {
  if (!steps || !weightKg) return 0;
  return Math.round(steps * 0.000414 * weightKg);
};

// Defensive normaliser — accepts legacy string-only slot values from older API responses.
const normaliseDay = (raw) => {
  if (!raw) return { ...EMPTY_DAY };
  if (typeof raw === "string") return { breakfast: "", lunch: "", dinner: raw, snacks: [] };
  return {
    breakfast: raw.breakfast || "",
    lunch: raw.lunch || "",
    dinner: raw.dinner || "",
    snacks: Array.isArray(raw.snacks) ? raw.snacks.filter(Boolean) : [],
  };
};

export default function Planner() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [recipes, setRecipes] = useState([]);
  const [plan, setPlan] = useState({});
  const [steps, setSteps] = useState({});
  const [target, setTarget] = useState(null); // daily kcal target
  const [weight, setWeight] = useState(null); // kg, for burn calc
  const [logs, setLogs] = useState({}); // {Mon: [{log_id, name, kcal, source}], ...}
  const [todayKey, setTodayKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSteps, setSavingSteps] = useState(false);
  const [addingDay, setAddingDay] = useState(null);
  const [addedDays, setAddedDays] = useState({});
  const [addingWeek, setAddingWeek] = useState(null); // "basket" | "share" | null
  const [presets, setPresets] = useState([]);
  const [presetModal, setPresetModal] = useState(null); // { mode: "save" | "apply", day }
  const [budgetPlanOpen, setBudgetPlanOpen] = useState(false);
  const stepsTimerRef = useRef(null);

  // Auto-open the Budget Plan modal when arriving via /app/planner?builder=1
  // (that's the target of the sidebar "Budget plan" menu item).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("builder") === "1") {
      setBudgetPlanOpen(true);
      // Clean the URL so a refresh doesn't re-open the modal.
      const cleaned = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleaned);
    }
  }, []);

  useEffect(() => {
    if (!isPro) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [r, p, c, lg, mp] = await Promise.all([
          api.get("/recipes"),
          api.get("/planner"),
          api.get("/me/calorie-target").catch(() => null),
          api.get("/calorie-log/week").catch(() => ({ data: { by_day: {}, today: null } })),
          api.get("/meal-presets").catch(() => ({ data: { presets: [] } })),
        ]);
        setRecipes(r.data);
        const rawSlots = p.data.slots || {};
        const normSlots = {};
        DAYS.forEach((d) => { normSlots[d] = normaliseDay(rawSlots[d]); });
        setPlan(normSlots);
        setSteps(p.data.steps || {});
        setTarget(c?.data?.result?.daily_kcal ?? null);
        setWeight(c?.data?.calorie_profile?.weight_kg ?? null);
        setLogs(lg?.data?.by_day || {});
        setTodayKey(lg?.data?.today || null);
        setPresets(mp?.data?.presets || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [isPro]);

  const setSlot = async (day, mealKey, recipeId) => {
    const dayData = normaliseDay(plan[day]);
    const nextDay = { ...dayData, [mealKey]: recipeId };
    const next = { ...plan, [day]: nextDay };
    setPlan(next);
    setAddedDays((prev) => { const { [day]: _drop, ...rest } = prev; return rest; });
    setSaving(true);
    try {
      await api.put("/planner", { slots: next });
    } catch {
      toast.error("Could not save planner");
    } finally {
      setSaving(false);
    }
  };

  const addSnack = async (day, recipeId) => {
    if (!recipeId) return;
    const dayData = normaliseDay(plan[day]);
    if (dayData.snacks.includes(recipeId)) return;
    const nextDay = { ...dayData, snacks: [...dayData.snacks, recipeId] };
    const next = { ...plan, [day]: nextDay };
    setPlan(next);
    setSaving(true);
    try {
      await api.put("/planner", { slots: next });
    } catch {
      toast.error("Could not save planner");
    } finally {
      setSaving(false);
    }
  };

  const removeSnack = async (day, recipeId) => {
    const dayData = normaliseDay(plan[day]);
    const nextDay = { ...dayData, snacks: dayData.snacks.filter((id) => id !== recipeId) };
    const next = { ...plan, [day]: nextDay };
    setPlan(next);
    setSaving(true);
    try {
      await api.put("/planner", { slots: next });
    } catch {
      toast.error("Could not save planner");
    } finally {
      setSaving(false);
    }
  };

  const copyYesterday = async (day) => {
    const idx = DAYS.indexOf(day);
    if (idx <= 0) return; // Monday has no previous day in the weekly view
    const prevKey = DAYS[idx - 1];
    const prev = normaliseDay(plan[prevKey]);
    if (!prev.breakfast && !prev.lunch && !prev.dinner && prev.snacks.length === 0) {
      toast.info(`${prevKey} is empty — nothing to copy.`);
      return;
    }
    const nextDay = { ...prev, snacks: [...prev.snacks] };
    const next = { ...plan, [day]: nextDay };
    setPlan(next);
    setAddedDays((prev2) => { const { [day]: _drop, ...rest } = prev2; return rest; });
    setSaving(true);
    try {
      await api.put("/planner", { slots: next });
      toast.success(`Copied ${prevKey} → ${day}`);
    } catch {
      toast.error("Could not save planner");
    } finally {
      setSaving(false);
    }
  };

  const applyDayToEmpty = async (sourceDay) => {
    const src = normaliseDay(plan[sourceDay]);
    if (!src.breakfast && !src.lunch && !src.dinner && src.snacks.length === 0) return;
    const next = { ...plan };
    let count = 0;
    DAYS.forEach((d) => {
      if (d === sourceDay) return;
      const ex = normaliseDay(plan[d]);
      if (!ex.breakfast && !ex.lunch && !ex.dinner && ex.snacks.length === 0) {
        next[d] = { ...src, snacks: [...src.snacks] };
        count += 1;
      }
    });
    if (count === 0) {
      toast.info("No empty days to fill.");
      return;
    }
    setPlan(next);
    setSaving(true);
    try {
      await api.put("/planner", { slots: next });
      toast.success(`Applied ${sourceDay} to ${count} empty day${count > 1 ? "s" : ""}.`);
    } catch {
      toast.error("Could not save planner");
    } finally {
      setSaving(false);
    }
  };

  const saveDayAsPreset = async (day, name) => {
    const slots = normaliseDay(plan[day]);
    if (!slots.breakfast && !slots.lunch && !slots.dinner && slots.snacks.length === 0) {
      toast.info("That day's empty — nothing to save.");
      return;
    }
    try {
      const { data } = await api.post("/meal-presets", { name, slots });
      setPresets((cur) => [data.preset, ...cur]);
      toast.success(`Saved preset "${data.preset.name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset");
    }
  };

  const deletePreset = async (presetId) => {
    try {
      await api.delete(`/meal-presets/${presetId}`);
      setPresets((cur) => cur.filter((p) => p.preset_id !== presetId));
      toast.success("Preset deleted.");
    } catch {
      toast.error("Could not delete preset");
    }
  };

  const applyPresetToDay = async (presetId, day) => {
    try {
      const { data } = await api.post(`/meal-presets/${presetId}/apply`, { day });
      const normSlots = {};
      DAYS.forEach((d) => { normSlots[d] = normaliseDay(data.slots[d]); });
      setPlan(normSlots);
      setAddedDays((prev) => { const { [day]: _drop, ...rest } = prev; return rest; });
      toast.success(`Applied "${data.preset_name}" to ${day}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply preset");
    }
  };

  // Debounced steps save (1 request after 600ms idle, regardless of re-renders)
  const saveSteps = (next) => {
    if (stepsTimerRef.current) clearTimeout(stepsTimerRef.current);
    stepsTimerRef.current = setTimeout(async () => {
      setSavingSteps(true);
      try {
        await api.put("/planner/steps", { steps: next });
      } catch {
        toast.error("Could not save steps");
      } finally {
        setSavingSteps(false);
      }
    }, 600);
  };

  const setDaySteps = (day, n) => {
    const clean = Math.max(0, Math.min(200000, parseInt(n || 0, 10) || 0));
    const next = { ...steps, [day]: clean };
    setSteps(next);
    saveSteps(next);
  };

  const addDayToBasket = async (day) => {
    const d = normaliseDay(plan[day]);
    const recipeIds = [d.breakfast, d.lunch, d.dinner, ...d.snacks].filter(Boolean);
    if (!recipeIds.length) return;
    setAddingDay(day);
    let lastList = null;
    let totalAdded = 0;
    let createdAny = false;
    try {
      for (const recipeId of recipeIds) {
        const { data } = await api.post("/shopping-lists/add-meal", { recipe_id: recipeId });
        lastList = data.list;
        totalAdded += data.added_count || 0;
        if (data.created) createdAny = true;
      }
      if (!lastList) return;
      setAddedDays((prev) => ({ ...prev, [day]: lastList.list_id }));
      const verb = createdAny ? "Created basket" : "Added to";
      toast.success(`${verb} "${lastList.name}" · +${totalAdded} ingredients from ${recipeIds.length} meal${recipeIds.length > 1 ? "s" : ""}`, {
        duration: 6000,
        action: { label: "View list", onClick: () => { window.location.href = `/app/shopping/${lastList.list_id}`; } },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to basket");
    } finally {
      setAddingDay(null);
    }
  };

  // One-tap: gather every meal from every day of the week and push it into a single
  // shopping list. Used by the two big buttons above the planner grid.
  const collectWeekRecipeIds = () => {
    const out = [];
    for (const dayKey of DAYS) {
      const d = normaliseDay(plan[dayKey]);
      for (const rid of [d.breakfast, d.lunch, d.dinner, ...d.snacks]) {
        if (rid && !out.includes(rid)) out.push(rid);
      }
    }
    return out;
  };

  const addWeekToBasket = async (mode = "basket") => {
    const recipeIds = collectWeekRecipeIds();
    if (!recipeIds.length) {
      toast.error("Plan some meals first, then I can build the list.");
      return;
    }
    setAddingWeek(mode);
    let lastList = null;
    let totalAdded = 0;
    let createdAny = false;
    try {
      for (const recipeId of recipeIds) {
        const { data } = await api.post("/shopping-lists/add-meal", { recipe_id: recipeId });
        lastList = data.list;
        totalAdded += data.added_count || 0;
        if (data.created) createdAny = true;
      }
      if (!lastList) return;
      // Mark every day as added so the per-day buttons update.
      const filledIds = {};
      for (const dayKey of DAYS) {
        const d = normaliseDay(plan[dayKey]);
        if ([d.breakfast, d.lunch, d.dinner, ...d.snacks].some(Boolean)) {
          filledIds[dayKey] = lastList.list_id;
        }
      }
      setAddedDays((prev) => ({ ...prev, ...filledIds }));

      if (mode === "share") {
        // Ask the backend for a share token, then use native share (or copy fallback).
        try {
          const { data: shareResp } = await api.post(`/shopping-lists/${lastList.list_id}/share`);
          const publicUrl = `${window.location.origin}/api/share/link/${shareResp.share_token}`;
          const title = `🛒 ${lastList.name} — Shop, Cal & Meal`;
          const introText = `${totalAdded} items for a full 7-day meal plan. Tap the link to open the live list — you can tick items off as you shop, no login needed.`;

          if (navigator.share) {
            try {
              await navigator.share({ title, text: introText, url: publicUrl });
              toast.success("Shared 📲 — hand this to whoever's doing the shop", { duration: 6000 });
              return;
            } catch (err) {
              if (err && err.name === "AbortError") return;
            }
          }
          try {
            await navigator.clipboard.writeText(publicUrl);
            toast.success(`Share link copied — ${totalAdded} items ready for whoever's at the shops`, {
              duration: 8000,
              action: { label: "Open list", onClick: () => { window.location.href = `/app/shopping/${lastList.list_id}`; } },
            });
          } catch {
            toast.success(`Share link: ${publicUrl}`, { duration: 12000 });
          }
        } catch {
          toast.error("Basket built, but couldn't make a share link. Open the list to share from there.");
        }
      } else {
        const verb = createdAny ? "Created basket" : "Added to";
        toast.success(`${verb} "${lastList.name}" · +${totalAdded} ingredients from ${recipeIds.length} meal${recipeIds.length > 1 ? "s" : ""} · Ready for delivery / click & collect`, {
          duration: 7000,
          action: { label: "Open list", onClick: () => { window.location.href = `/app/shopping/${lastList.list_id}`; } },
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the full week to basket");
    } finally {
      setAddingWeek(null);
    }
  };

  const recipeKcalById = (id) => {
    if (!id) return 0;
    return recipes.find((x) => x.recipe_id === id)?.calories_per_serving ?? 0;
  };

  const dayMealKcal = (day) => {
    const d = normaliseDay(plan[day]);
    return (
      recipeKcalById(d.breakfast) +
      recipeKcalById(d.lunch) +
      recipeKcalById(d.dinner) +
      d.snacks.reduce((s, id) => s + recipeKcalById(id), 0)
    );
  };

  const dayHasAnyMeal = (day) => {
    const d = normaliseDay(plan[day]);
    return !!(d.breakfast || d.lunch || d.dinner || d.snacks.length);
  };

  const dailyLoggedKcal = (day) => (logs[day] || []).reduce((s, l) => s + (l.kcal || 0), 0);
  const dailyCalories = (day) => {
    const total = dayMealKcal(day) + dailyLoggedKcal(day);
    return total > 0 ? total : null;
  };
  const dailyBurned = (day) => burnedKcal(steps[day] || 0, weight);
  const dailyNet = (day) => {
    const eaten = dailyCalories(day) || 0;
    return eaten - dailyBurned(day);
  };
  const dailyRemaining = (day) => {
    if (!target) return null;
    return target - dailyNet(day);
  };

  const removeLog = async (log_id, day) => {
    // Optimistic update
    setLogs((prev) => ({ ...prev, [day]: (prev[day] || []).filter((l) => l.log_id !== log_id) }));
    try {
      await api.delete(`/calorie-log/${log_id}`);
    } catch {
      toast.error("Could not remove entry");
    }
  };

  // Week aggregates
  const weekTotal = DAYS.reduce((s, d) => s + (dailyCalories(d) || 0), 0);
  const weekBurned = DAYS.reduce((s, d) => s + dailyBurned(d), 0);
  const weekSteps = DAYS.reduce((s, d) => s + (steps[d] || 0), 0);
  const weekStepGoal = STEP_GOAL * 7;
  const filled = DAYS.filter(d => dayHasAnyMeal(d)).length;
  const progressPct = Math.round((filled / DAYS.length) * 100);
  const weekTarget = target ? target * 7 : null;
  const streak = computeStreak(steps);

  if (!isPro) {
    return (
      <div className="space-y-8 ff-fade-in">
        <header>
          <div className="ff-label">This week</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Weekly planner
          </h1>
        </header>
        <ProPaywall
          feature="The weekly Planner is a Pro feature"
          message="Plan each day, track your steps, see your true net calories and auto-build a shopping list. Available on every Shop, Cal & Meal Pro plan — starting at £4.99/month."
        />
      </div>
    );
  }

  return (
    <div className="space-y-8 ff-fade-in">
      {presetModal && (
        <PresetModal
          mode={presetModal.mode}
          day={presetModal.day}
          presets={presets}
          recipes={recipes}
          onSave={saveDayAsPreset}
          onApply={applyPresetToDay}
          onDelete={deletePreset}
          onClose={() => setPresetModal(null)}
        />
      )}
      {budgetPlanOpen && (
        <BudgetPlanModal
          defaultTargetKcal={target}
          onClose={() => setBudgetPlanOpen(false)}
          onDone={async (result) => {
            setBudgetPlanOpen(false);
            // Refetch planner + recipes so the newly-generated meals appear.
            try {
              const [p, r] = await Promise.all([
                api.get("/planner"),
                api.get("/recipes"),
              ]);
              const rawSlots = p.data.slots || {};
              const normSlots = {};
              DAYS.forEach((d) => { normSlots[d] = normaliseDay(rawSlots[d]); });
              setPlan(normSlots);
              setRecipes(r.data);
              toast.success(
                `Your week is ready · ~£${result.total_estimated_cost_gbp.toFixed(2)}`,
                { duration: 6000 }
              );
            } catch {
              toast.error("Plan built but couldn't refresh — reload the page.");
            }
          }}
        />
      )}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ff-label">This week</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Weekly planner
          </h1>
          <p className="text-[#5C6B61] mt-2">
            Pencil in a recipe for each day. Log your steps to see your true net intake{saving || savingSteps ? " · saving…" : ""}.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="ff-card px-5 py-4 text-center" data-testid="week-kcal-card">
            <div className="ff-label">Net kcal</div>
            <div className="text-3xl font-semibold text-[#A65541] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {(weekTotal - weekBurned) || "—"}
            </div>
            {weekTarget && (
              <div className="text-[11px] text-[#8B9690] mt-1">of {weekTarget.toLocaleString()} target</div>
            )}
            {weekBurned > 0 && (
              <div className="text-[11px] text-[#3B5A3E] mt-0.5">−{weekBurned} burned</div>
            )}
          </div>
          <div className="ff-card px-5 py-4 text-center" data-testid="week-steps-card">
            <div className="ff-label">Steps</div>
            <div className="text-3xl font-semibold text-[#3B5A3E] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {weekSteps ? weekSteps.toLocaleString() : "—"}
            </div>
            <div className="text-[11px] text-[#8B9690] mt-1">of {weekStepGoal.toLocaleString()} goal</div>
          </div>
          <div className="ff-card px-5 py-4 text-center">
            <div className="ff-label">Planned</div>
            <div className="text-3xl font-semibold text-[#3B5A3E] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{filled}/7</div>
          </div>
        </div>
      </header>

      {/* Budget meal-plan CTA — always visible, but shrinks to a compact
          "Regenerate" pill once the week is filled so users can always tweak it. */}
      {filled <= 2 ? (
        <button
          type="button"
          onClick={() => setBudgetPlanOpen(true)}
          data-testid="open-budget-plan-modal"
          className="w-full rounded-2xl p-5 flex items-center gap-4 text-left transition hover:shadow-lg"
          style={{ background: "linear-gradient(135deg, #E8F0E9 0%, #F6EED9 100%)", border: "1px solid #C8D8CA" }}
        >
          <div className="flex-none w-14 h-14 rounded-2xl bg-white flex items-center justify-center text-3xl shadow-sm">
            🎯
          </div>
          <div className="flex-1 min-w-0">
            <div className="ff-label text-[#3B5A3E]">Fastest way to plan a week</div>
            <div className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Build me a plan for a set budget
            </div>
            <div className="text-sm text-[#5C6B61] mt-0.5">
              Pick your budget &amp; store — AI fills the week with cheap, healthy meals.
            </div>
          </div>
          <div className="text-2xl text-[#3B5A3E] flex-none">→</div>
        </button>
      ) : (
        <div className="space-y-3 -mt-2">
          {/* One-tap 7-day shopping — the two big buttons the user asked for. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="week-shop-cta">
            <button
              type="button"
              onClick={() => addWeekToBasket("basket")}
              disabled={addingWeek !== null}
              data-testid="week-add-all-basket"
              className="rounded-2xl px-4 py-4 text-left transition hover:shadow-md disabled:opacity-60 disabled:cursor-wait flex items-center gap-3"
              style={{ background: "linear-gradient(135deg,#E8F0E9 0%,#C8D8CA 100%)", border: "1px solid #A6C0AA" }}
              title="Push every ingredient from the whole week into one shopping list"
            >
              <span className="text-3xl flex-none" aria-hidden>🛒</span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {addingWeek === "basket" ? "Building your basket…" : "Add all 7 days to my basket"}
                </div>
                <div className="text-xs text-[#3B5A3E] mt-0.5">
                  Everything you need for the week · one click · ready for delivery or click &amp; collect
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => addWeekToBasket("share")}
              disabled={addingWeek !== null}
              data-testid="week-add-all-share"
              className="rounded-2xl px-4 py-4 text-left transition hover:shadow-md disabled:opacity-60 disabled:cursor-wait flex items-center gap-3"
              style={{ background: "linear-gradient(135deg,#FBEFEA 0%,#F6E0D5 100%)", border: "1px solid #E9C7B5" }}
              title="Build the list AND generate a share link so someone else can shop it"
            >
              <span className="text-3xl flex-none" aria-hidden>🧺</span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {addingWeek === "share" ? "Building & making share link…" : "Add all & share with family"}
                </div>
                <div className="text-xs text-[#A65541] mt-0.5">
                  For going in-store · sends a live tick-off link to whoever&apos;s at the shops
                </div>
              </div>
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => setBudgetPlanOpen(true)}
              data-testid="open-budget-plan-modal"
              className="text-xs font-semibold uppercase tracking-wider bg-white border border-[#C8D8CA] text-[#3B5A3E] hover:bg-[#E8F0E9] px-3 py-2 rounded-full transition flex items-center gap-1.5"
              title="Change budget / diet / calories and rebuild the week"
            >
              <span aria-hidden>🎯</span> Rebuild budget plan
            </button>
          </div>
        </div>
      )}

      {/* Streak strip */}
      <div
        className="ff-card p-4 flex items-center gap-3 flex-wrap"
        data-testid="streak-ribbon"
        style={{ background: streak > 0 ? "linear-gradient(135deg,#FBEFEA,#F3F0EA)" : "#F3F0EA" }}
      >
        <span className="text-2xl">{streak >= 3 ? "🔥" : streak >= 1 ? "👟" : "💤"}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#A65541] font-medium">
            {streak > 0 ? `${streak}-day streak` : "No streak yet"}
          </div>
          <div className="text-sm text-[#2A332C]">
            {streak > 0
              ? `Consecutive days hitting ${STEP_GOAL.toLocaleString()}+ steps. Keep it going!`
              : `Hit ${STEP_GOAL.toLocaleString()} steps to start your streak.`}
          </div>
        </div>
        {weight == null && (
          <Link to="/app/calories" className="text-xs font-medium text-[#3B5A3E] underline-offset-4 hover:underline">
            Set weight for accurate kcal →
          </Link>
        )}
      </div>

      {target ? null : (
        <div className="ff-card p-4 flex items-center gap-3 flex-wrap" data-testid="no-target-ribbon">
          <span className="text-2xl">🎯</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-[#5C6B61] font-medium">No daily target yet</div>
            <div className="text-sm text-[#2A332C]">Set one and we&apos;ll show how each recipe fits your goal.</div>
          </div>
          <Link to="/app/calories" className="ff-btn-primary text-sm">Set target</Link>
        </div>
      )}

      <div className="w-full h-1.5 bg-[#F3F0EA] rounded-full overflow-hidden">
        <div className="h-full bg-[#3B5A3E] transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      {loading ? (
        <div className="text-[#5C6B61]">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {DAYS.map((d) => {
            const dayMeals = normaliseDay(plan[d]);
            const loggedKcal = dailyLoggedKcal(d);
            const mealKcal = dayMealKcal(d);
            const kcal = mealKcal + loggedKcal;
            const hasIntake = kcal > 0;
            const burned = dailyBurned(d);
            const net = kcal - burned;
            const remaining = target ? target - net : null;
            const dayLogs = logs[d] || [];
            const isToday = d === todayKey;
            const anyMealPicked = dayHasAnyMeal(d);
            return (
              <div key={d} className="ff-card p-5" data-testid={`planner-day-${d}`}>
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h3 className="font-medium flex items-center gap-2 min-w-0" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {d}
                    {isToday && <span className="text-[10px] font-medium uppercase tracking-wider bg-[#3B5A3E] text-white px-2 py-0.5 rounded-full">Today</span>}
                  </h3>
                  <div className="flex items-center gap-1.5 flex-none">
                    {(() => {
                      const i = DAYS.indexOf(d);
                      if (i <= 0) return null; // Monday has no previous day
                      const prev = normaliseDay(plan[DAYS[i - 1]]);
                      const prevHas = !!(prev.breakfast || prev.lunch || prev.dinner || prev.snacks.length);
                      if (!prevHas || anyMealPicked) return null; // only show on empty day with a non-empty previous day
                      return (
                        <button
                          type="button"
                          onClick={() => copyYesterday(d)}
                          data-testid={`copy-yesterday-${d}`}
                          title={`Copy meals from ${DAYS[i - 1]}`}
                          className="text-[10px] font-medium uppercase tracking-wider text-[#3B5A3E] bg-[#E8F0E9] hover:bg-[#D5E5D7] px-2 py-1 rounded-full transition whitespace-nowrap"
                        >
                          ↶ Copy {DAYS[i - 1]}
                        </button>
                      );
                    })()}
                    {hasIntake && <DayBadge kcal={kcal} target={target} burned={burned} />}
                  </div>
                </div>

                {/* Meal slots: Breakfast / Lunch / Dinner */}
                <div className="space-y-2" data-testid={`meal-slots-${d}`}>
                  {MEAL_SLOTS.map(({ key, label, icon }) => {
                    const picked = dayMeals[key];
                    const pickedRecipe = picked ? recipes.find((rc) => rc.recipe_id === picked) : null;
                    // Filter recipes to the ones tagged for THIS slot (plus any
                    // untagged / "other" recipes so nothing goes missing), and
                    // always keep the currently-picked recipe available.
                    const slotRecipes = recipes.filter((rc) => {
                      const mt = (rc.meal_type || "").toLowerCase();
                      if (rc.recipe_id === picked) return true;
                      if (mt === key) return true;
                      if (!mt || mt === "other") return true;
                      return false;
                    });
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-base flex-none w-5 text-center" aria-hidden>{icon}</span>
                        <div className="flex-1 min-w-0 relative">
                          <select
                            className="ff-input text-sm w-full"
                            value={picked || ""}
                            onChange={(e) => setSlot(d, key, e.target.value)}
                            data-testid={`planner-select-${d}-${key}`}
                            aria-label={`${label} for ${d}`}
                          >
                            <option value="">— {label.toLowerCase()} —</option>
                            {slotRecipes.map((rc) => (
                              <option key={rc.recipe_id} value={rc.recipe_id}>{rc.title}</option>
                            ))}
                          </select>
                        </div>
                        {pickedRecipe?.calories_per_serving ? (
                          <span className="text-[10px] text-[#5C6B61] whitespace-nowrap flex-none">
                            {pickedRecipe.calories_per_serving} kcal
                          </span>
                        ) : null}
                      </div>
                    );
                  })}

                  {/* Snacks (multiple) */}
                  <SnackPicker
                    day={d}
                    recipes={recipes}
                    snackIds={dayMeals.snacks}
                    onAdd={(rid) => addSnack(d, rid)}
                    onRemove={(rid) => removeSnack(d, rid)}
                  />
                </div>

                {/* Steps input — visible on every day */}
                <StepInput day={d} value={steps[d] || 0} onChange={(n) => setDaySteps(d, n)} burned={burned} />

                {dayLogs.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid={`logged-scans-${d}`}>
                    <div className="ff-label">Logged today</div>
                    {dayLogs.map((l) => (
                      <div key={l.log_id} className="flex items-center justify-between gap-2 text-xs bg-[#F3F0EA] rounded-lg px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#2A332C] truncate">
                            {l.source === "photo" ? "📷" : l.source === "barcode" ? "📊" : (l.source || "").startsWith("saved:") ? "★" : "•"} {l.name}
                          </div>
                          <div className="text-[#5C6B61]">{l.kcal} kcal</div>
                        </div>
                        <button
                          onClick={() => removeLog(l.log_id, d)}
                          className="text-[#A65541] text-[11px] hover:underline"
                          data-testid={`remove-log-${l.log_id}`}
                          aria-label="Remove entry"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3">
                  <SavedFoodsQuickLog
                    dayKey={d}
                    variant="compact"
                    title="Quick-log saved food"
                    emptyHint={`No saved foods yet — scan one to log to ${d} fast.`}
                    testidPrefix={`day-saved-foods-${d}`}
                    onLogged={() => {
                      // Refetch the week so the new log row appears under the day.
                      api.get("/calorie-log/week").then(({ data }) => setLogs(data.by_day || {})).catch(() => {});
                    }}
                  />
                </div>

                {hasIntake && target && (
                  <DayMeter kcal={kcal} burned={burned} target={target} />
                )}
                {target && (
                  <CaloriesLeft remaining={remaining} eaten={kcal} burned={burned} target={target} day={d} />
                )}
                {hasIntake && target && burned > 0 && (
                  <div className="text-[11px] text-[#5C6B61] mt-2" data-testid={`net-kcal-${d}`}>
                    {kcal} eaten − {burned} burned = <strong>{net} net</strong>
                  </div>
                )}
                {anyMealPicked && (
                  <button
                    onClick={() => addDayToBasket(d)}
                    disabled={addingDay === d || !!addedDays[d]}
                    data-testid={`add-day-to-list-${d}`}
                    className={`w-full mt-3 rounded-full px-3 py-2 text-xs font-medium transition ${addedDays[d] ? "bg-[#E8F0E9] text-[#3B5A3E]" : "bg-[#3B5A3E] text-white hover:bg-[#2c4530]"} disabled:opacity-60`}
                  >
                    {addedDays[d] ? "✓ In basket" : addingDay === d ? "Adding…" : "🛒 Add all meals to shopping list"}
                  </button>
                )}
                {/* Day actions: presets + apply-to-empty */}
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`day-actions-${d}`}>
                  {anyMealPicked && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPresetModal({ mode: "save", day: d })}
                        data-testid={`save-preset-${d}`}
                        className="text-[10px] font-medium uppercase tracking-wider text-[#3B5A3E] bg-white border border-[#D5E2D8] hover:bg-[#F3F0EA] px-2 py-1 rounded-full transition"
                      >
                        💾 Save preset
                      </button>
                      {DAYS.some((dd) => {
                        if (dd === d) return false;
                        const e = normaliseDay(plan[dd]);
                        return !e.breakfast && !e.lunch && !e.dinner && e.snacks.length === 0;
                      }) && (
                        <button
                          type="button"
                          onClick={() => applyDayToEmpty(d)}
                          data-testid={`apply-to-empty-${d}`}
                          className="text-[10px] font-medium uppercase tracking-wider text-[#3B5A3E] bg-white border border-[#D5E2D8] hover:bg-[#F3F0EA] px-2 py-1 rounded-full transition"
                        >
                          ↳ Apply to empty days
                        </button>
                      )}
                    </>
                  )}
                  {!anyMealPicked && presets.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPresetModal({ mode: "apply", day: d })}
                      data-testid={`use-preset-${d}`}
                      className="text-[10px] font-medium uppercase tracking-wider text-[#A65541] bg-white border border-[#E5D3CB] hover:bg-[#FBEFEA] px-2 py-1 rounded-full transition"
                    >
                      📋 Use preset
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BudgetPlanModal({ onClose, onDone, defaultTargetKcal }) {
  const [budget, setBudget] = useState(30);
  const [store, setStore] = useState("Tesco");
  const [people, setPeople] = useState(2);
  const [diet, setDiet] = useState("any");
  const [avoid, setAvoid] = useState("");
  const [customAvoid, setCustomAvoid] = useState("");
  // Common UK allergens / avoidances — one tap toggles them on/off.
  // Users can also type-in a custom chip via the "+ Add custom" pill.
  const AVOID_PRESETS = [
    "Nuts", "Peanuts", "Dairy", "Gluten", "Eggs",
    "Shellfish", "Fish", "Soy", "Sesame",
    "Pork", "Beef", "Lamb", "Alcohol", "Spicy", "Mushrooms",
  ];
  const avoidTokens = (avoid || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const avoidTokensLower = avoidTokens.map((t) => t.toLowerCase());
  const toggleAvoid = (token) => {
    const lc = token.toLowerCase();
    let next;
    if (avoidTokensLower.includes(lc)) {
      next = avoidTokens.filter((t) => t.toLowerCase() !== lc);
    } else {
      next = [...avoidTokens, token];
    }
    setAvoid(next.join(", "));
  };
  const addCustomAvoid = () => {
    const t = customAvoid.trim();
    if (!t) return;
    if (!avoidTokensLower.includes(t.toLowerCase())) {
      setAvoid([...avoidTokens, t].join(", "));
    }
    setCustomAvoid("");
  };
  const removeAvoidToken = (token) => {
    setAvoid(avoidTokens.filter((t) => t !== token).join(", "));
  };
  const [snacks, setSnacks] = useState(false);
  const [targetKcal, setTargetKcal] = useState(defaultTargetKcal || 2000);
  const [reserveSnackKcal, setReserveSnackKcal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Preload saved diet/avoid/store on open — remembers user's last choices.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/preferences");
        if (data.diet_pref) setDiet(data.diet_pref);
        if (typeof data.avoid_pref === "string") setAvoid(data.avoid_pref);
        if (data.preferred_store) setStore(data.preferred_store);
      } catch {}
      setPrefsLoaded(true);
    })();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const meal_types = ["breakfast", "lunch", "dinner"];
      if (snacks) meal_types.push("snacks");
      const { data } = await api.post("/planner/generate-budget-plan", {
        budget_gbp: Number(budget),
        store,
        people: Number(people),
        meal_types,
        diet,
        avoid,
        target_kcal_per_day: Number(targetKcal) || null,
        reserve_snack_kcal: Number(reserveSnackKcal) || 0,
      });
      onDone(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Couldn't build a plan right now. Try again in a sec.");
      toast.error(msg);
      setBusy(false);
    }
  };

  const stores = ["Tesco", "Sainsbury's", "ASDA", "Morrisons", "Waitrose", "Aldi", "Lidl", "M&S", "Iceland"];
  const diets = [
    { k: "any", l: "Any (mixed)" },
    { k: "vegetarian", l: "Vegetarian" },
    { k: "vegan", l: "Vegan" },
    { k: "pescatarian", l: "Pescatarian" },
    { k: "high-protein", l: "High protein" },
    { k: "keto", l: "Keto" },
    { k: "atkins", l: "Atkins" },
    { k: "carnivore", l: "Carnivore" },
    { k: "low-carb", l: "Low-carb" },
    { k: "slimming-world", l: "Slimming World" },
    { k: "weight-watchers", l: "Weight Watchers" },
    { k: "intermittent-fasting", l: "Intermittent fasting" },
    { k: "paleo", l: "Paleo" },
    { k: "mediterranean", l: "Mediterranean" },
    { k: "gluten-free", l: "Gluten-free" },
    { k: "dairy-free", l: "Dairy-free" },
    { k: "low-fodmap", l: "Low-FODMAP" },
    { k: "diabetic-friendly", l: "Diabetic-friendly" },
    { k: "halal", l: "Halal" },
    { k: "kosher", l: "Kosher" },
  ];

  return createPortal((
    <div
      data-testid="budget-plan-modal"
      className="fixed inset-0 z-[70] bg-[#2A332C]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="ff-card max-w-md w-full p-6 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-xl flex-none">🎯</span>
          <div className="flex-1 min-w-0">
            <div className="ff-label text-[#3B5A3E]">Budget meal plan</div>
            <h3
              className="text-xl md:text-2xl font-semibold mt-1 leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Feed the week on a set budget
            </h3>
          </div>
        </div>
        <p className="text-sm text-[#5C6B61] mt-4 leading-relaxed">
          We&apos;ll build a 7-day meal plan that fits your budget at your favourite UK supermarket, then fill the planner and the shopping list.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="ff-label block mb-1.5">Weekly budget (£)</label>
            <div className="flex gap-2">
              {[20, 30, 45, 60, 80].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setBudget(v)}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${
                    Number(budget) === v ? "bg-[#3B5A3E] text-white" : "bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                  }`}
                  data-testid={`budget-preset-${v}`}
                >£{v}</button>
              ))}
            </div>
            <input
              type="number" min={5} max={500} step={1}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="ff-input mt-2"
              data-testid="budget-input"
              aria-label="Weekly budget"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ff-label block mb-1.5">Store</label>
              <select className="ff-input" value={store} onChange={(e) => setStore(e.target.value)} data-testid="budget-store-select">
                {stores.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="ff-label block mb-1.5">Household</label>
              <select className="ff-input" value={people} onChange={(e) => setPeople(e.target.value)} data-testid="budget-people-select">
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="ff-label block mb-1.5">Diet</label>
            <select className="ff-input" value={diet} onChange={(e) => setDiet(e.target.value)} data-testid="budget-diet-select">
              {diets.map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
            </select>
          </div>
          <div>
            <label className="ff-label block mb-1.5">
              Daily kcal per person
              {defaultTargetKcal ? <span className="ml-1 text-[#8B9690] font-normal normal-case">· from your goal</span> : null}
            </label>
            <div className="flex gap-2">
              {[1500, 1800, 2000, 2200, 2500].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setTargetKcal(v)}
                  className={`flex-1 py-2 rounded-full text-xs font-semibold transition ${
                    Number(targetKcal) === v ? "bg-[#3B5A3E] text-white" : "bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                  }`}
                  data-testid={`kcal-preset-${v}`}
                >{v}</button>
              ))}
            </div>
            <input
              type="number" min={800} max={6000} step={50}
              value={targetKcal}
              onChange={(e) => setTargetKcal(e.target.value)}
              className="ff-input mt-2"
              data-testid="target-kcal-input"
              aria-label="Daily calorie target per person"
            />
            <p className="text-[11px] text-[#8B9690] mt-1 leading-relaxed">
              Meals sized to hit this daily total (±10%).
            </p>
          </div>
          <div>
            <label className="ff-label block mb-1.5">
              Reserve kcal for snacks/drinks
              <span className="ml-1 text-[#8B9690] font-normal normal-case">· leave room for extras</span>
            </label>
            <div className="flex gap-2">
              {[0, 150, 250, 400, 600].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setReserveSnackKcal(v)}
                  className={`flex-1 py-2 rounded-full text-xs font-semibold transition ${
                    Number(reserveSnackKcal) === v ? "bg-[#3B5A3E] text-white" : "bg-[#F3F0EA] text-[#3B5A3E] hover:bg-[#E5E2DA]"
                  }`}
                  data-testid={`reserve-preset-${v}`}
                >
                  {v === 0 ? "None" : v}
                </button>
              ))}
            </div>
            <input
              type="range" min={0} max={1000} step={50}
              value={reserveSnackKcal}
              onChange={(e) => setReserveSnackKcal(e.target.value)}
              className="mt-3 w-full accent-[#3B5A3E]"
              data-testid="reserve-snack-slider"
              aria-label="Kcal reserved for snacks per day"
            />
            <p className="text-[11px] text-[#8B9690] mt-1 leading-relaxed">
              Your daily cap is <strong>{Number(targetKcal) || 0}</strong> kcal. Of that, <strong>{Math.max(600, Number(targetKcal) - Number(reserveSnackKcal))}</strong> kcal is planned into meals and <strong>{reserveSnackKcal || 0}</strong> kcal is held back for your own snacks, drinks or biscuits. Meals + snacks stay within your cap.
            </p>
          </div>
          <div>
            <label className="ff-label block mb-1.5">Avoid / allergies</label>
            {/* Selected chips (removable) */}
            {avoidTokens.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="avoid-selected-chips">
                {avoidTokens.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => removeAvoidToken(t)}
                    data-testid={`avoid-selected-${t.toLowerCase()}`}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#3B5A3E] text-white flex items-center gap-1 hover:bg-[#2A4230] transition"
                    title={`Remove "${t}"`}
                  >
                    {t} <span aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}
            {/* Preset chips (tap to toggle) */}
            <div className="flex flex-wrap gap-1.5" data-testid="avoid-preset-chips">
              {AVOID_PRESETS.map((p) => {
                const active = avoidTokensLower.includes(p.toLowerCase());
                return (
                  <button
                    type="button"
                    key={p}
                    onClick={() => toggleAvoid(p)}
                    data-testid={`avoid-preset-${p.toLowerCase()}`}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                      active
                        ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                        : "bg-white text-[#2A332C] border-[#DADCD3] hover:border-[#3B5A3E] hover:text-[#3B5A3E]"
                    }`}
                  >
                    {active ? "✓ " : "+ "}{p}
                  </button>
                );
              })}
            </div>
            {/* Custom-add input (for anything not in the preset list). */}
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                placeholder="Something else? Type & add"
                value={customAvoid}
                onChange={(e) => setCustomAvoid(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAvoid(); } }}
                maxLength={40}
                className="ff-input flex-1 text-sm"
                data-testid="avoid-custom-input"
              />
              <button
                type="button"
                onClick={addCustomAvoid}
                disabled={!customAvoid.trim()}
                data-testid="avoid-custom-add"
                className="text-xs font-semibold px-3 py-2 rounded-full bg-[#3B5A3E] text-white disabled:opacity-40 hover:bg-[#2A4230] transition whitespace-nowrap"
              >
                + Add
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2A332C]" data-testid="budget-snacks-toggle">
            <input type="checkbox" checked={snacks} onChange={(e) => setSnacks(e.target.checked)} className="accent-[#3B5A3E]" />
            <span>Include snacks too</span>
          </label>
          <p className="text-[11px] text-[#8B9690] leading-relaxed">
            💡 Prices are ~ approximate UK own-brand estimates. Your real basket total in your supermarket app is the source of truth.
          </p>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} disabled={busy} className="ff-btn-ghost" data-testid="budget-plan-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !budget}
            className="ff-btn-primary disabled:opacity-50 flex items-center gap-2"
            data-testid="budget-plan-submit"
          >
            {busy ? "Building… (5–15s)" : "Build my week →"}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function PresetModal({ mode, day, presets, recipes, onSave, onApply, onDelete, onClose }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const recipeTitleById = (id) => recipes.find((r) => r.recipe_id === id)?.title || "—";

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    await onSave(day, trimmed);
    setBusy(false);
    onClose();
  };

  return createPortal((
    <div
      data-testid="preset-modal"
      className="fixed inset-0 z-[70] bg-[#2A332C]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="ff-card max-w-md w-full p-6 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "save" ? (
          <>
            <div className="ff-label text-[#3B5A3E]">Save {day} as a preset</div>
            <h3 className="text-xl font-semibold mt-1 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Name this meal day
            </h3>
            <p className="text-sm text-[#5C6B61] mb-4 leading-relaxed">
              Saves all four slots from <strong>{day}</strong>. Use it later on any day with one tap.
            </p>
            <input
              type="text"
              className="ff-input"
              placeholder="e.g. Lean day, Cheat day, Workout day"
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="preset-name-input"
              autoFocus
            />
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={onClose} className="ff-btn-ghost" data-testid="preset-modal-cancel">Cancel</button>
              <button
                onClick={handleSave}
                disabled={!name.trim() || busy}
                className="ff-btn-primary disabled:opacity-50"
                data-testid="preset-save-confirm"
              >
                {busy ? "Saving…" : "Save preset"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="ff-label text-[#A65541]">Apply preset to {day}</div>
            <h3 className="text-xl font-semibold mt-1 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Pick a saved meal day
            </h3>
            {presets.length === 0 ? (
              <p className="text-sm text-[#5C6B61]">You haven&apos;t saved any presets yet. Fill a day and tap &quot;💾 Save preset&quot; first.</p>
            ) : (
              <ul className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="preset-list">
                {presets.map((p) => {
                  const s = p.slots || {};
                  const summary = [
                    s.breakfast && `🌅 ${recipeTitleById(s.breakfast)}`,
                    s.lunch && `🌞 ${recipeTitleById(s.lunch)}`,
                    s.dinner && `🌙 ${recipeTitleById(s.dinner)}`,
                    (s.snacks || []).length > 0 && `🍿 ${s.snacks.length} snack${s.snacks.length > 1 ? "s" : ""}`,
                  ].filter(Boolean).join(" · ");
                  return (
                    <li key={p.preset_id} className="flex items-center gap-2 border border-[#E5E2DA] rounded-2xl p-3" data-testid={`preset-item-${p.preset_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#2A332C] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</div>
                        <div className="text-[11px] text-[#5C6B61] truncate">{summary || "Empty"}</div>
                      </div>
                      <button
                        onClick={async () => { await onApply(p.preset_id, day); onClose(); }}
                        className="text-[11px] font-medium px-3 py-1.5 rounded-full bg-[#3B5A3E] text-white hover:bg-[#2c4530]"
                        data-testid={`preset-apply-${p.preset_id}`}
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => onDelete(p.preset_id)}
                        className="text-[11px] text-[#A65541] hover:underline"
                        data-testid={`preset-delete-${p.preset_id}`}
                        aria-label="Delete preset"
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={onClose} className="ff-btn-ghost" data-testid="preset-modal-close">Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
}

function SnackPicker({ day, recipes, snackIds, onAdd, onRemove }) {
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState("");

  const handleAdd = () => {
    if (!selected) return;
    onAdd(selected);
    setSelected("");
    setPicking(false);
  };

  const recipeById = (id) => recipes.find((rc) => rc.recipe_id === id);
  // Snacks list: only show recipes tagged as snacks (plus untagged/other as
  // a safety net so nothing goes missing) and exclude ones already added.
  const available = recipes.filter((rc) => {
    if (snackIds.includes(rc.recipe_id)) return false;
    const mt = (rc.meal_type || "").toLowerCase();
    return mt === "snack" || mt === "drink" || !mt || mt === "other";
  });

  return (
    <div data-testid={`snacks-block-${day}`}>
      <div className="flex items-center gap-2">
        <span className="text-base flex-none w-5 text-center" aria-hidden>🍿</span>
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
          {snackIds.length === 0 && !picking && (
            <span className="text-xs text-[#8B9690]">No snacks yet</span>
          )}
          {snackIds.map((id) => {
            const r = recipeById(id);
            return (
              <span
                key={id}
                data-testid={`snack-chip-${day}-${id}`}
                className="inline-flex items-center gap-1 bg-[#FBEFEA] text-[#A65541] text-[11px] font-medium px-2 py-1 rounded-full"
              >
                <span className="truncate max-w-[110px]">{r?.title || "Snack"}</span>
                {r?.calories_per_serving ? (
                  <span className="opacity-70">· {r.calories_per_serving}</span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  className="opacity-70 hover:opacity-100 ml-0.5"
                  aria-label="Remove snack"
                  data-testid={`remove-snack-${day}-${id}`}
                >
                  ✕
                </button>
              </span>
            );
          })}
          {picking ? (
            <div className="flex items-center gap-1 w-full">
              <select
                className="ff-input text-xs flex-1"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                data-testid={`snack-select-${day}`}
                autoFocus
              >
                <option value="">— pick a snack —</option>
                {available.map((rc) => (
                  <option key={rc.recipe_id} value={rc.recipe_id}>{rc.title}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!selected}
                className="text-[11px] font-medium px-2 py-1 rounded-full bg-[#3B5A3E] text-white disabled:opacity-50"
                data-testid={`snack-add-confirm-${day}`}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setPicking(false); setSelected(""); }}
                className="text-[11px] text-[#8B9690] hover:text-[#2A332C]"
                aria-label="Cancel"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="text-[11px] font-medium text-[#3B5A3E] underline-offset-4 hover:underline"
              data-testid={`add-snack-${day}`}
            >
              + Add snack
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CaloriesLeft({ remaining, eaten, burned, target, day }) {
  // Hide if nothing logged yet and full target left — keeps the card calm at the start of the day.
  if (eaten === 0 && burned === 0) return null;
  const over = remaining < 0;
  return (
    <div
      data-testid={`kcal-remaining-${day}`}
      className="mt-3 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-between"
      style={{
        background: over ? "#FBE5DE" : "#E8F0E9",
        color: over ? "#A33D26" : "#3B5A3E",
      }}
    >
      <span>{over ? "Over by" : "Left today"}</span>
      <span className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
        {Math.abs(remaining).toLocaleString()} <span className="opacity-70 font-normal">kcal</span>
      </span>
    </div>
  );
}

function StepInput({ day, value, onChange, burned }) {
  const pct = Math.min(100, Math.round((value / STEP_GOAL) * 100));
  const hit = value >= STEP_GOAL;
  return (
    <div className="mt-3" data-testid={`steps-block-${day}`}>
      <div className="flex items-center gap-2">
        <span className="text-base">👟</span>
        <input
          type="number"
          inputMode="numeric"
          min="0"
          max="200000"
          step="100"
          value={value || ""}
          placeholder="0 steps"
          onChange={(e) => onChange(e.target.value)}
          data-testid={`steps-input-${day}`}
          className="ff-input flex-1 text-sm"
        />
        {burned > 0 && (
          <span className="text-xs text-[#3B5A3E] font-medium whitespace-nowrap" data-testid={`burned-${day}`}>
            −{burned} kcal
          </span>
        )}
      </div>
      <div className="h-1 bg-[#F3F0EA] rounded-full overflow-hidden mt-1.5">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, background: hit ? "#3B5A3E" : "#A65541" }}
        />
      </div>
      <div className="text-[10px] text-[#8B9690] mt-0.5">
        {hit ? `🎯 ${STEP_GOAL.toLocaleString()} hit` : `${pct}% of ${STEP_GOAL.toLocaleString()} goal`}
      </div>
    </div>
  );
}

function DayBadge({ kcal, target, burned }) {
  if (kcal == null) {
    return (
      <div className="text-[10px] text-[#8B9690] text-right leading-tight" data-testid="day-kcal-badge">
        <div className="uppercase tracking-wider">No food logged</div>
      </div>
    );
  }
  const net = kcal - (burned || 0);
  const burnedNote = burned ? ` (− ${burned} burned)` : "";
  if (!target) {
    return (
      <div
        className="text-right leading-tight"
        data-testid="day-kcal-badge"
        title={`Eaten today: ${kcal} kcal${burnedNote}. Set a daily calorie target in Calories to track progress.`}
      >
        <div className="text-[10px] text-[#8B9690] uppercase tracking-wider">Eaten</div>
        <div className="text-base font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif", color: "#A65541" }}>
          {net} <span className="text-[10px] font-normal text-[#8B9690]">kcal</span>
        </div>
      </div>
    );
  }
  const ratio = net / target;
  const fg = ratio <= 0.85 ? "#3B5A3E" : ratio <= 1.05 ? "#8A6B0E" : "#A33D26";
  return (
    <div
      className="text-right leading-tight whitespace-nowrap"
      data-testid="day-kcal-badge"
      title={`Eaten ${kcal} kcal${burnedNote} → ${net} net of your ${target} kcal daily target.`}
    >
      <div className="text-[10px] text-[#8B9690] uppercase tracking-wider">
        Eaten / Target
      </div>
      <div className="text-base font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
        <span style={{ color: fg }}>{net}</span>
        <span className="text-[#8B9690] font-normal"> / </span>
        <span className="text-[#2A332C]">{target}</span>
        <span className="text-[10px] font-normal text-[#8B9690] ml-1">kcal</span>
      </div>
    </div>
  );
}

function DayMeter({ kcal, burned, target }) {
  const net = kcal - (burned || 0);
  const pct = Math.max(0, Math.min(100, Math.round((net / target) * 100)));
  const over = net > target;
  return (
    <div className="mt-3" data-testid="day-meter">
      <div className="h-1.5 bg-[#F3F0EA] rounded-full overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, background: over ? "#C36952" : "#3B5A3E" }}
        />
      </div>
      <div className="text-[10px] text-[#8B9690] mt-1">{pct}% of daily target (net)</div>
    </div>
  );
}

// Count consecutive days BACK from today (or the latest non-zero day) that hit STEP_GOAL.
function computeStreak(steps) {
  // Use today's weekday to anchor, but count any contiguous hits in the week ending today.
  const todayIdx = (new Date().getDay() + 6) % 7; // Mon=0..Sun=6
  let streak = 0;
  for (let i = todayIdx; i >= 0; i--) {
    if ((steps[DAYS[i]] || 0) >= STEP_GOAL) streak++;
    else break;
  }
  return streak;
}
